import React, { useEffect } from 'react';
import { Form, Switch, Typography, Alert } from 'antd';
import type { FormProps } from 'antd';
import AppFormContainer from '@nxsys/components/AppFormContainer';
import AppInput from '@nxsys/components/AppInput';
import AppSelect from '@nxsys/components/AppSelect';
import AppSelectSync from '@nxsys/components/AppSelectSync';
import {
  defaultCisStatus,
  defaultPaymentFrequency,
} from '@nxsys/constants/AppConstants';

const taxYearOptions = [
  { label: '2026', value: 2026 },
  { label: '2025', value: 2025 },
];
import AppCard from '@nxsys/components/AppCard';
import { UserSearchIcon } from '@nxsys/components/icons';
import { find } from 'lodash';
import { useDispatch } from 'react-redux';
import { useSelector } from 'react-redux';
import { AppDispatch, RootState } from '@nxsys/store';
import AppButton from '@nxsys/components/AppButton';
import { kidAction } from '@nxsys/store/slices/kid/kidSlice';
import {
  KidFormData,
  KidParams,
  OptionType,
  CandidateInfo,
  CompanyPaymentInfo,
} from '@nxsys/store/slices/kid/types';
import { showCurrencyByCountry } from '@nxsys/utils';
import { toNumber } from '@nxsys/utils/number-helper';
import AppCurrencyInput from '@nxsys/components/AppCurrencyInput';
import AppRateInput from '@nxsys/components/AppRateInput';

// AppSelect is labelInValue, so once a year is picked the form holds
// { label, value } while the initial value is still a plain number.
const toTaxYear = (value: KidFormData['taxYear']): number | undefined =>
  toNumber(
    typeof value === 'object' && value !== null ? value.value : value,
  ) || undefined;

const CreateForm: React.FC = () => {
  const [form] = Form.useForm();
  const dispatch = useDispatch<AppDispatch>();
  const { isAgency, user, payrollCountry } = useSelector(
    (state: RootState) => state.appStore,
  );
  const agencyPortalId = isAgency ? user?.agencyId?.id : null;
  const agencyCompanyRef = isAgency ? user?.agencyContactRef?.companyRef : null;

  const handlePreview = async () => {
    const values = form.getFieldsValue(true);
    const {
      isCandidateExist,
      agencyId = null,
      payFrequency = null,
      noOfHours = null,
      candidateId = null,
      emailId = null,
      payRatePerHour = null,
      companyId = null,
      candidateName,
      cisStatus,
      margin,
      constOfMaterial,
      taxYear,
      VATRegNo,
    } = values;

    const params = {
      isCandidateExist,
      companyId: companyId?.value || null,
      candidateId: isCandidateExist ? candidateId?.value : null,
      candidateName: !isCandidateExist ? candidateName : candidateId?.label,
      agencyId: agencyId?.value || agencyPortalId || null,
      emailId,
      payFrequency: payFrequency?.label || null,
      noOfHours,
      payRatePerHour,
      cisStatus: cisStatus || null,
      margin: margin || 0,
      constOfMaterial: constOfMaterial || 0,
      taxYear: toTaxYear(taxYear),
      VATRegNo,
    };
    dispatch(kidAction.onPreviewKIDSubcontractor(params));
  };

  const convertFormDataToParams = (values: KidFormData): KidParams => {
    const {
      isCandidateExist,
      agencyId = null,
      payFrequency = null,
      noOfHours = null,
      candidateId = null,
      emailId = null,
      payRatePerHour = null,
      companyId = null,
      candidateName,
      cisStatus,
      margin,
      constOfMaterial,
      taxYear,
      VATRegNo,
    } = values;

    return {
      isCandidateExist,
      companyId: companyId?.value || null,
      candidateId: isCandidateExist ? candidateId?.value : null,
      candidateName: !isCandidateExist ? candidateName : candidateId?.label,
      agencyId: agencyId?.value || agencyPortalId || null,
      emailId: emailId || '',
      payFrequency: (payFrequency?.value as string) || null,
      noOfHours: toNumber(noOfHours) || 0,
      payRatePerHour: toNumber(payRatePerHour) || 0,
      cisStatus: cisStatus?.value2 || null,
      margin: toNumber(margin) || 0,
      constOfMaterial: toNumber(constOfMaterial) || 0,
      taxYear: toTaxYear(taxYear),
      VATRegNo,
    };
  };

  const handlePreviewAction = () => {
    const values = form.getFieldsValue(true);

    const params = convertFormDataToParams(values);
    dispatch(kidAction.setPreviewData({ formData: params }));

    handlePreview();
  };

  const onFinishFailed: FormProps['onFinishFailed'] = (errorInfo) => {
    console.log('Failed:', errorInfo);
  };

  const onValuesChange = (changedValues: Partial<KidFormData>) => {
    if ('isCandidateExist' in changedValues) {
      form.resetFields();
      form.setFieldsValue({
        isCandidateExist: changedValues.isCandidateExist,
        ...(agencyCompanyRef && {
          companyId: {
            value: agencyCompanyRef.id,
            label: agencyCompanyRef.companyName,
          },
        }),
      });
    }
    const values = form.getFieldsValue(true);
    const params = convertFormDataToParams(values);
    dispatch(kidAction.setPreviewData({ formData: params }));
  };

  const handleChangeCandidateExist = (value: CandidateInfo) => {
    const { payFrequency, setting, email } = value?.fullData || {};
    const payFrequencyOption =
      find(defaultPaymentFrequency, (item) => item?.value === payFrequency) ||
      null;
    const cisStatusOption = find(
      defaultCisStatus,
      (item) => item?.value === setting?.cisStatus,
    );
    // Use candidate/subcontractor defaults
    const currentValues = form.getFieldsValue(true);
    form.setFieldsValue({
      candidateId: value,
      payFrequency: payFrequencyOption || currentValues?.payFrequency,
      cisStatus: cisStatusOption || currentValues?.cisStatus,
      margin: setting?.margin ? Number(setting?.margin) : currentValues?.margin,
      emailId: email || currentValues?.email,
      VATRegNo: setting?.VATRegNo,
    });
    onValuesChange({});
  };

  const handleChangeCompany = (value: CompanyPaymentInfo) => {
    const { paymentFrequency, cisStatus, margin } = value?.fullData || {};
    const payFrequencyOption =
      find(
        defaultPaymentFrequency,
        (item) => item?.value === paymentFrequency,
      ) || null;
    const cisStatusOption = find(
      defaultCisStatus,
      (item) => item?.value === cisStatus,
    );
    // Pre-fill company defaults (only if current fields are empty)
    const currentValues = form.getFieldsValue(true);
    const marginCompany = margin ? Number(margin) : null;
    form.setFieldsValue({
      companyId: value,
      payFrequency: currentValues.payFrequency ||
        payFrequencyOption || { label: 'Weekly', value: 'WEEKLY' },
      cisStatus: currentValues.cisStatus || cisStatusOption || null,
      margin: currentValues?.margin || marginCompany || 15,
    });
    onValuesChange({});
  };

  useEffect(() => {
    if (agencyCompanyRef) {
      handleChangeCompany({
        value: agencyCompanyRef.id,
        label: agencyCompanyRef.companyName,
        fullData: agencyCompanyRef,
      } as CompanyPaymentInfo);
    }
  }, [agencyCompanyRef]);

  return (
    <>
      <div className="flex justify-between items-center px-5 py-3 border-b border-neutral-200">
        <Typography.Title level={4} className="mt-0 mb-0 flex-shrink-0">
          Create KID
        </Typography.Title>
        <AppButton
          key="calculate"
          name="Calculate"
          variant="outlined"
          type="primary"
          disabled={true}
          className="invisible"
        />
      </div>
      <div className="w-full max-w-full p-[20px]">
        <AppFormContainer
          form={form}
          initialValues={{
            isCandidateExist: false,
            agencyId: null,
            payFrequency: null,
            margin: null,
            candidateId: null,
            candidateName: '',
            emailId: '',
            noOfHours: 40,
            payRatePerHour: 15,
            constOfMaterial: 0,
            cisStatus: null,
            companyId: user?.agencyContactRef?.companyRef?.id
              ? {
                  label: user?.agencyContactRef?.companyRef?.companyName,
                  value: user?.agencyContactRef?.companyRef?.id,
                }
              : null,
            taxYear: 2026,
            VATRegNo: '',
          }}
          onFinish={() => handlePreviewAction()}
          onFinishFailed={onFinishFailed}
          onValuesChange={onValuesChange}
          className="w-full max-w-full"
        >
          <Form.Item name="VATRegNo" hidden>
            <input type="hidden" />
          </Form.Item>
          <AppCard className="p-[16px] mb-[16px]">
            <div className="flex flex-row items-center">
              <UserSearchIcon />
              <div className="ml-[16px]">
                <p className="font-[600] text-primary-600">Candidate Exist?</p>
                <Form.Item
                  name="isCandidateExist"
                  initialValue={false}
                  labelCol={{ className: 'text-neutral-900' }}
                  label={<span className="text-neutral-900">Yes</span>}
                  className="flex items-center center-form mb-0"
                >
                  <Switch size="small" className="ml-[8px]" />
                </Form.Item>
              </div>
            </div>
          </AppCard>

          {/* Default settings message for existing candidates */}
          <Form.Item
            shouldUpdate={(prev, curr) =>
              prev.isCandidateExist !== curr.isCandidateExist
            }
            noStyle
          >
            {({ getFieldValue }) =>
              getFieldValue('isCandidateExist') && (
                <Alert
                  message="Default settings from the candidate and company will be applied if not selected below"
                  type="info"
                  showIcon
                  className="mb-4"
                />
              )
            }
          </Form.Item>

          <Form.Item
            label="Tax Year"
            name="taxYear"
            rules={[{ required: true, message: 'Please select tax year' }]}
          >
            <AppSelect
              options={taxYearOptions}
              placeholder="Select tax year"
              className="!min-w-0 !w-full !max-w-full"
            />
          </Form.Item>

          <Form.Item
            label="Company Name"
            name="companyId"
            rules={[{ required: true, message: 'Please select company' }]}
          >
            <AppSelectSync
              type="company"
              apiType="searchCompany"
              placeholder="Select company"
              className="!min-w-0 !w-full !max-w-full"
              disabled={!!agencyCompanyRef}
              {...(agencyCompanyRef && {
                defaultValue: {
                  value: agencyCompanyRef.id,
                  label: agencyCompanyRef.companyName,
                },
              })}
              onChangeValue={(value: OptionType | OptionType[] | null) => {
                if (value && !Array.isArray(value)) {
                  handleChangeCompany(value as CompanyPaymentInfo);
                }
              }}
              paramFilter={{
                paymentModel: 'CIS',
              }}
            />
          </Form.Item>

          <Form.Item
            shouldUpdate={(prev, curr) =>
              prev.isCandidateExist !== curr.isCandidateExist ||
              prev.companyId !== curr.companyId
            }
            noStyle
          >
            {({ getFieldValue }) => {
              const isCandidateExist = getFieldValue('isCandidateExist');
              const company = getFieldValue('companyId');
              return isCandidateExist ? (
                <Form.Item
                  label="Candidate Name"
                  name="candidateId"
                  rules={[
                    { required: true, message: 'Please select candidate' },
                  ]}
                >
                  <AppSelectSync
                    type="emp&sub"
                    apiType="searchEmployeeSubcontractV2"
                    placeholder="Select Candidate"
                    className="!min-w-0 !w-full !max-w-full"
                    onChangeValue={(
                      value: OptionType | OptionType[] | null,
                    ) => {
                      if (value && !Array.isArray(value)) {
                        handleChangeCandidateExist(value as CandidateInfo);
                      }
                    }}
                    paramFilter={{
                      paymentModel: 'CIS',
                      agencyId: agencyPortalId ? agencyPortalId : undefined,
                      companyPaymentIds: company ? [company.value] : undefined,
                    }}
                  />
                </Form.Item>
              ) : (
                <Form.Item
                  label="Candidate Name"
                  name="candidateName"
                  rules={[
                    { required: true, message: 'Please input candidate name' },
                  ]}
                >
                  <AppInput
                    placeholder="Enter candidate name"
                    className="w-full max-w-full"
                  />
                </Form.Item>
              );
            }}
          </Form.Item>

          {!isAgency && (
            <Form.Item
              label="Select Agency"
              name="agencyId"
              // rules={[{ required: true, message: 'Please select agency' }]}
            >
              <AppSelectSync
                type="agency"
                apiType="searchAgency"
                placeholder="Select agency"
                className="!min-w-0 !w-full !max-w-full"
                onChangeValue={(value: OptionType | OptionType[] | null) => {
                  if (value && !Array.isArray(value)) {
                    form.setFieldsValue({ agencyId: value });
                  }
                }}
              />
            </Form.Item>
          )}

          <Form.Item
            label="Email ID"
            name="emailId"
            // rules={[{ required: true, message: 'Please input Email' }]}
          >
            <AppInput
              placeholder="Enter email id"
              className="w-full max-w-full"
              allowCapitalize={false}
            />
          </Form.Item>

          <Form.Item
            label="Pay Frequency"
            name="payFrequency"
            rules={[{ required: true, message: 'Please input pay frequency' }]}
            id="payFrequency"
          >
            <AppSelect
              options={defaultPaymentFrequency}
              placeholder="Select pay frequency"
              className="!min-w-0 !w-full !max-w-full"
            />
          </Form.Item>

          <Form.Item
            label="Pay Rate/Hours"
            name="payRatePerHour"
            rules={[{ required: true, message: 'Please input pay rate/hours' }]}
          >
            <AppCurrencyInput
              prefix={showCurrencyByCountry(payrollCountry)}
              placeholder="Enter pay rate/hours"
              className="w-full max-w-full"
            />
          </Form.Item>

          <Form.Item
            label="No of Hours"
            name="noOfHours"
            rules={[{ required: true, message: 'Please input no of hours' }]}
          >
            <AppRateInput
              placeholder="Enter no of hours"
              className="w-full max-w-full"
            />
          </Form.Item>

          <Form.Item
            label="Cost of Material"
            name="constOfMaterial"
            rules={[
              { required: false, message: 'Please input cost of material' },
            ]}
          >
            <AppCurrencyInput
              prefix={showCurrencyByCountry(payrollCountry)}
              placeholder="Enter cost of material"
              className="w-full max-w-full"
            />
          </Form.Item>

          <Form.Item label="Margin" name="margin" rules={[{ required: false }]}>
            <AppCurrencyInput
              prefix={showCurrencyByCountry(payrollCountry)}
              placeholder="Enter margin"
              className="w-full max-w-full"
            />
          </Form.Item>

          <Form.Item
            label="CIS Status"
            name="cisStatus"
            // rules={[{ required: true, message: 'Please input cis status' }]}
            id="cisStatus"
          >
            <AppSelect
              options={defaultCisStatus}
              placeholder="Select status"
              className="!min-w-0 !w-full !max-w-full"
            />
          </Form.Item>
          <div className="flex justify-end border-t border-neutral-200 pt-5 ml-[-20px] mr-[-20px] px-5">
            <AppButton
              key="calculate"
              name="Calculate"
              variant="outlined"
              type="primary"
              onClick={() => handlePreviewAction()}
            />
          </div>
        </AppFormContainer>
      </div>
    </>
  );
};

export default CreateForm;
